import { useLogoutMutation, useRefreshTokenMutation } from '@api/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { Logger } from '@unistocks/shared/services/logger.service';
import { AppActions, selectToken } from '@unistocks/shared/store/root.slice';
import { today } from '@unistocks/shared/utils/util';
import { AuthTokenDto } from '@unistocks/types/auth';
import { Duration } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';

/**
 * Automatically refreshes the token when it is about to expire.
 */
const timeoutFactor = 0.85;
// https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#maximum_delay_value
const maxTimeoutValue = 2147483647;

export function isTokenValid(token?: AuthTokenDto) {
  if (!token?.expire_time) return false;

  const now = today().toMillis();

  return now < token.expire_time;
}

/**
 * Calculates the timeout value for the token refresh.
 * @param storedAt
 * @param expirationTime
 * @returns Timeout value in milliseconds
 */
export function calcTimeout(storedAt: number, expirationTime: number): number {
  const now = today().toMillis();

  const delta = (expirationTime - storedAt) * timeoutFactor - (now - storedAt);
  const duration = Math.max(0, delta);

  return Math.min(duration, maxTimeoutValue);
}

/**
 * Check if the token is valid and refresh it if it is about to expire.
 * @returns @isValid true if the token is valid, false otherwise
 * @returns @isReady true if the token is valid or a refresh has been attempted
 */
export const useAuthToken = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const token = useAppSelector(selectToken);
  const { mutate: logout, isPending } = useLogoutMutation();
  const isValid = useMemo(() => (token && isTokenValid(token)) ?? false, [token]);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const { mutate: refresh } = useRefreshTokenMutation({
    onSuccess: refreshedToken => {
      dispatch(AppActions.setToken(refreshedToken));
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
    },
    onError: e => {
      Logger.error(e, 'Failed to refresh token');
      dispatch(AppActions.setToken());
    }
  });

  const isReady = isValid || !token?.refresh_token;

  useEffect(() => {
    if (token?.refresh_token) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const timeoutMs = calcTimeout(token.stored_at, token.expire_time);
      timeoutRef.current = setTimeout(() => refresh(token.refresh_token), timeoutMs);

      if (isValid)
        Logger.debug(
          `Token is valid, will refresh in ${Duration.fromMillis(timeoutMs).toFormat(
            "d 'days', hh:mm:ss"
          )}`
        );
      else Logger.debug(`Token is invalid, will refresh immediately`);
    }

    if (!isValid && !isPending && !!token?.access_token && !token.refresh_token) {
      Logger.debug('Token is invalid and no refresh token is available, logging out');
      logout();
    }
  }, [isPending, isValid, logout, refresh, token]);

  useEffect(() => {
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    isValid,
    isReady
  };
};
